import { message } from 'antd';
import { push } from 'connected-react-router';
import { selectLanguage } from 'containers/LanguageProvider/selectors';
import { setAlcoholState } from 'containers/MainLayout/actions';
import { makeSelectLocation } from 'containers/MainLayout/selectors';
import { Location } from 'history';
import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import translations from 'translations';
import {
  Pagination,
  ProductSortField,
  QueryCategoryDetailArgs,
  QueryProductSearchArgs,
  SortDirection,
} from 'types/schema';
import { getDescriptionById, getProducts, requestNewProductItem } from 'utils/apollo';
import { LANGUAGES } from 'utils/constants';
import { createSearchQueryfromSlug, getPathSegmentFromSlug } from 'utils/getPathSegmentFromSlug';
import utilsMessages from 'utils/messages';
import * as qs from 'utils/queryString';
import { pathnameChecker } from 'utils/url';
import { getCategoryIDFromSlug } from 'utils/urls/categoriesPathSegment';
import { isRecommendationTag } from 'utils/utilities';
import { getBrands, getItemsInSameProductGroupsApi } from '../../utils/apollo';
import { getLocalOriginName } from '../../utils/getLocalOriginName';
import MainLayoutActionTypes from '../MainLayout/constants';
import { selectBuyer, selectCategoriesObject } from '../MainLayout/selectors';
import {
  description as descriptionActions,
  products as productsActions,
  searchByFilter,
  setAggregationsData,
  setKamereoProducts,
  setSuggestedProducts,
} from './actions';
import { ActionTypes } from './constants';
import { handleParseQueryParams } from './utils';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';

const MAX_PAGE_SIZE = 40;

function handleSearchValueFromLocation(location: Location) {
  /* Transform the pathname with hyphen into separate segment's slug value */
  const segment = getPathSegmentFromSlug(location);

  /* create a query string that can be parsed into an qs object. */
  const searchQuery = createSearchQueryfromSlug(location, segment);
  const searchValue: QueryProductSearchArgs | undefined = searchQuery.length > 1 ? qs.parse(searchQuery) : undefined;

  return { searchValue: searchValue, segment: segment };
}

function* getItemsInSameProductGroups(productIds) {
  const loggedIn = !!localStorage.getItem('token');
  const regionLocal = localStorage.getItem('region');
  let buyer = yield select(selectBuyer());
  if (!buyer || !Object?.keys(buyer)?.length) {
    yield take(MainLayoutActionTypes.FETCH_BUYER_SUCCESS);
    buyer = yield select(selectBuyer());
  }
  const response = yield call(getItemsInSameProductGroupsApi, {
    productIds: productIds,
    pagination: {
      page: 0,
      size: 1000,
    },
    regionCode: loggedIn ? buyer?.region : regionLocal || 'HCM',
  });
  if (!response.errors) {
    yield put(setKamereoProducts(response.data));
  }
}

function* getBrandsData(brandIds) {
  const brandResponse = yield call(getBrands, {
    brandIds: brandIds,
    pagination: { page: 0, size: 100 },
  });
  if (!brandResponse.errors) {
    yield put(
      setAggregationsData({
        loading: false,
        brands: brandResponse?.data?.map((brand) => ({
          label: brand.name,
          value: Number(brand.id),
          image: brand.imageUrl,
        })),
      }),
    );
  } else {
    yield put(
      setAggregationsData({
        loading: false,
        brands: [],
      }),
    );
  }
}

function* parseAggregationsData(aggregationsData) {
  const lang = localStorage.getItem('lang') || 'vi';

  const categoriesObject = yield select(selectCategoriesObject());
  const originProducts = aggregationsData?.origins;
  const brandIds = aggregationsData?.brandIds;
  const parsedOriginProducts = originProducts.map((origin) => {
    const label = getLocalOriginName(origin || '');
    return {
      value: origin,
      label: label,
    };
  });
  const categoryIdsAndSubCategoryIds = aggregationsData?.categoryIdsAndSubCategoryIds;
  const parsedSubCategories = categoryIdsAndSubCategoryIds.reduce((acc, categoriesId) => {
    if (
      !categoriesObject?.[categoriesId]?.isParentCategory &&
      !categoriesObject?.[categoriesId]?.name.toLowerCase().includes('other')
    ) {
      acc.push({
        label: categoriesObject?.[categoriesId]?.nameLocal,
        value: categoriesObject?.[categoriesId]?.id,
      });
    }
    return acc;
  }, []);
  if (parsedSubCategories?.length && parsedSubCategories?.length > 1) {
    parsedSubCategories.unshift({ label: lang === 'vi' ? 'Tất cả' : 'All', value: 0 });
  } else if (parsedSubCategories?.length === 1) {
    const location = yield select(makeSelectLocation());
    const { searchValue } = handleSearchValueFromLocation(location);
    const newUrl = `${window.location.pathname}?${qs.stringify({ ...searchValue })}`;
    window.history.pushState({}, '', newUrl);
  }
  yield put(
    setAggregationsData({
      loading: true,
      subCategories: parsedSubCategories || [],
      origins: parsedOriginProducts || [],
    }),
  );
  if (brandIds?.length) {
    yield fork(getBrandsData, brandIds);
  } else {
    yield put(
      setAggregationsData({
        loading: false,
        brands: [],
      }),
    );
  }
}

function* generateAggregationData(aggregationsData, search) {
  let currentAggregationsData = JSON.parse(JSON.stringify(aggregationsData));
  const { query, ...rest } = search.filter;
  const shouldInitAggregationDataFromBaseQuery = Object.entries(rest).some(([key, value]) => {
    if (key === 'categoryIds') {
      return value?.length && value?.[0] !== 0;
    }
    return value?.length;
  });
  if (shouldInitAggregationDataFromBaseQuery) {
    const response = yield call(getProducts, {
      filter: {
        query: query,
      },
      pagination: {
        page: 0,
        size: 1,
      },
      aggregationOptions: {
        includeOrigins: true,
        includeBrands: true,
        includeCategoriesAndSubCategories: true,
      },
      sort: [],
    });
    if (!response?.errors) {
      currentAggregationsData = response?.aggregationsData;
    }
  }
  yield fork(parseAggregationsData, currentAggregationsData);
}

function* handleMarketProduct(
  search: QueryProductSearchArgs,
  localLang: string,
  alternateLang: string,
  isInitPage?: boolean,
) {
  const requestStartTime = Date.now();
  let requestParams: QueryProductSearchArgs = JSON.parse(JSON.stringify(search));
  if (!search.sort.length && !search?.filter?.query) {
    requestParams.sort = [{ field: ProductSortField.Name, order: SortDirection.Asc }];
  }
  if (search?.filter?.query) {
    requestParams = {
      ...requestParams,
      aggregationOptions: {
        includeOrigins: true,
        includeBrands: true,
        includeCategoriesAndSubCategories: true,
      },
    };
  }
  const requestPage = requestParams.pagination.page;
  requestParams.pagination = {
    ...requestParams.pagination,
    page: requestPage ? requestPage - 1 : 0,
  };
  const response = yield call(getProducts, requestParams);
  if (!response.errors) {
    if (search?.filter?.query) {
      const productIds = response?.data?.reduce((acc, item) => {
        acc.push(item.id);
        return acc;
      }, []);
      if (!response?.data?.length) {
        yield fork(fetchSuggestedProductWhenNoSearchResult);
      }
      if (isInitPage && productIds.length) {
        if (isInitPage && productIds?.length) {
          yield fork(getItemsInSameProductGroups, productIds);
        }
      }
      if (isInitPage) {
        yield fork(generateAggregationData, response.aggregationsData, search);
      }
    }
    const requestEndTime = Date.now();
    sendGA4Event(EventName.SEARCH_PAGE_SEARCH_LATENCY, {
      keyword: search?.filter?.query,
      duration: requestEndTime - requestStartTime,
    });

    yield handleMarketProductSuccess(response, localLang, alternateLang);
    if (isInitPage && response?.data?.length) {
      sendGA4Event(EventName.VIEW_SEARCH_RESULTS, {
        keyword: search?.filter?.query,
        result_count: response?.totalResults,
        page_number: search?.pagination?.page || 1,
        filter_state: search?.filter || {},
        sort_option: search?.sort || [],
      });
    }

    if (!response?.data?.length) {
      sendGA4Event(EventName.NO_RESULT_VIEW, {
        keyword: search?.filter?.query,
        filter_state: search?.filter || {},
      });
    }
  } else {
    yield put(productsActions.failure(response.errors));
  }
}

function* handleMarketProductSuccess(response: any, localLang: string, alternateLang: string) {
  yield put(
    productsActions.success({
      result: {
        ...response,
        localLang: localLang,
        alternateLang: alternateLang,
      },
    }),
  );
  const location = yield select(makeSelectLocation());
  const categoryID = getCategoryIDFromSlug(location.pathname.slice(1));
  const descriptionVariables = { id: categoryID as unknown as QueryCategoryDetailArgs['id'] };
  if (categoryID) {
    const description = yield call(getDescriptionById, descriptionVariables);
    if (!description.errors) {
      yield put(descriptionActions.success(description));
    } else {
      yield put(descriptionActions.failure(description.errors));
    }
  } else {
    yield put(descriptionActions.success(''));
  }
  const isUnder18FromLocalStorage = window.localStorage.getItem('isUnder18')
    ? window.localStorage.getItem('isUnder18') === 'true'
    : true;
  const isAlcoholCategory = response?.data?.some((item) => item?.category?.parent?.name === 'Alcohol');
  if (isUnder18FromLocalStorage && !location.search && isAlcoholCategory) {
    yield put(setAlcoholState(true));
  }
  window.localStorage.setItem('lang', localLang as string);
}

function* handleSearchByFilter(action) {
  const { payload: searchValue } = action;
  yield put(productsActions.request());
  const { locale: localLang } = yield select(selectLanguage);
  const alternateLang = localLang === LANGUAGES.Vi ? LANGUAGES.Alternate || 'en' : LANGUAGES.Vi;
  const page = searchValue?.pagination?.page || 0;
  const search: QueryProductSearchArgs = {
    ...searchValue,
    pagination: { page: page, size: MAX_PAGE_SIZE },
  };
  yield put({
    type: ActionTypes.SET_SEARCH,
    payload: {
      ...search,
    },
  });
  yield call(handleMarketProduct, search, localLang, alternateLang);
}

function* searchFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.APPLY_SEARCH);
    const queryParams = Object.entries(payload?.search?.filter).reduce((acc, [key, value]) => {
      if (Array.isArray(value) && value?.length) {
        acc[key] = value?.join(', ');
      }
      return acc;
    }, {} as any);
    if (!payload.isFromCategory) {
      const searchValue = JSON.parse(
        JSON.stringify({
          ...payload?.search,
          sort: payload?.sort || [],
        }),
      );
      if (searchValue?.filter?.subCategoryId?.[0] !== 0) {
        searchValue.filter.categoryIds = searchValue?.filter?.subCategoryId;
      }
      delete searchValue.filter.subCategoryId;

      const stringifySort = payload?.sort?.map(
        (sortItem) => `${sortItem.order === SortDirection.Desc ? '-' : ''}${sortItem.field}`,
      );
      const newUrl = `${window.location.pathname}?${qs.stringify({ ...payload.search, filter: { ...payload.search.filter, ...queryParams }, sort: stringifySort || undefined })}`;
      window.history.pushState({}, '', newUrl);
      yield put(searchByFilter(searchValue));
      // yield put(push({ search: qs.stringify(payload.search) }));
    } else {
      yield put(
        push({
          search: qs.stringify({
            pagination: payload.search.pagination,
          }),
        }),
      );
    }
    yield put({ type: ActionTypes.SET_SEARCH, payload: payload.search });
  }
}

function* initData(searchValue?: QueryProductSearchArgs) {
  yield put(productsActions.request());
  const { locale: localLang } = yield select(selectLanguage);
  const alternateLang = localLang === LANGUAGES.Vi ? LANGUAGES.Alternate || 'en' : LANGUAGES.Vi;
  const page = searchValue?.pagination?.page || 0;
  const search: QueryProductSearchArgs = {
    sort: [],
    filter: {},
    ...searchValue,
    pagination: { page: page, size: MAX_PAGE_SIZE },
  };

  if (
    searchValue?.filter?.tags?.[0] &&
    ['NEW_PRODUCTS', 'GYOMU_SUPER', 'THUONG_HIEU_KAMEREO', 'BEST_DEALS', 'BEST_PRICES'].includes(
      searchValue?.filter?.tags?.[0],
    )
  ) {
    search.sort = [{ field: ProductSortField.CreatedAt, order: SortDirection.Desc }];
  }
  if (searchValue?.filter?.query) {
    search.sort = searchValue?.sort?.length ? searchValue?.sort : [];
  }
  if (!searchValue?.filter?.query && !searchValue?.filter?.tags?.length) {
    search.isBoostKamereoProducts = true;
  }
  yield put({
    type: ActionTypes.SET_SEARCH,
    payload: {
      ...search,
    },
  });
  yield call(handleMarketProduct, search, localLang, alternateLang, true);
}

function* recommendationData(searchValue?: QueryProductSearchArgs) {
  yield put(productsActions.request());
  yield put(descriptionActions.success(''));
  const pagination: Pagination = {
    size: searchValue?.pagination?.size || MAX_PAGE_SIZE,
    page: searchValue?.pagination?.page || 1,
  };

  yield put({
    type: ActionTypes.SET_SEARCH,
    payload: { pagination: pagination, filter: { tags: ['RECOMMENDED_FOR_YOU'] } },
  });

  const response = yield call(getProducts, {
    pagination: { ...pagination, page: pagination.page - 1 },
    filter: { isOnboardingBuyer: false, isRecommendation: true },
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
  });
  if (!response.errors) {
    yield put(productsActions.success({ result: response }));
  } else {
    yield put(productsActions.failure(response.errors));
  }
}

function* sendRequestNewProducts() {
  while (true) {
    const {
      payload: { input, cb },
    } = yield take(ActionTypes.SEND_REQUEST_NEW_PRODUCT);
    const response = yield call(requestNewProductItem, { input: input });
    if (!response.errors) {
      message.success(translations(utilsMessages.sendRequestSuccess));
      if (cb) {
        cb(true);
      }
    } else {
      message.error(translations(utilsMessages.sendRequestFail));
      if (cb) {
        cb();
      }
    }
  }
}

function* fetchSuggestedProductWhenNoSearchResult() {
  const regionLocal = localStorage.getItem('region');
  const buyer = yield select(selectBuyer());
  const params: QueryProductSearchArgs = {
    sort: [
      {
        field: ProductSortField.Name,
        order: SortDirection.Asc,
      },
    ],
    filter: {
      supplierNames: ['KAMEREO'],
      categoryName: null,
      tags: ['BEST_DEALS'],
      limitedTimeOfferPeriod: null,
      regionCode: buyer?.region ? buyer?.region : regionLocal,
    },
    pagination: {
      page: 0,
      size: 20,
    },
  };
  const response = yield call(getProducts, params);
  if (!response?.errors && response.data.length) {
    yield put(setSuggestedProducts(response.data));
  }
}

export default function* marketPageSaga() {
  yield put(setKamereoProducts([]));
  yield put(
    setAggregationsData({
      loading: true,
      subCategories: [],
      origins: [],
      brands: [],
    }),
  );
  const location = yield select(makeSelectLocation());
  if (pathnameChecker.isProductPage(location.pathname)) {
    yield put(push('/not-found'));
  } else if (location.pathname.endsWith('/')) {
    const pathWithoutSlash = location.pathname.replace(/\/+$/g, '');
    yield put(push(`${pathWithoutSlash}`));
  }
  const { searchValue, segment } = handleSearchValueFromLocation(location);
  let newSearchValue = searchValue;
  if (searchValue?.filter?.query) {
    const queryParsed: any = handleParseQueryParams(searchValue);
    newSearchValue = {
      ...queryParsed,
    };
  }
  const isRecommended = isRecommendationTag(segment?.tags);
  if (!isRecommended) {
    yield fork(initData, newSearchValue);
  } else {
    yield fork(recommendationData, searchValue);
  }
  yield takeLatest(ActionTypes.SEARCH_BY_FILTER, handleSearchByFilter);
  yield fork(searchFlow);
  yield fork(sendRequestNewProducts);
}
